import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import DetailScreen from "./DetailScreen";
import { getPageInfo } from "../../Services/StoreService";
import { useSearchContext } from "../../context/searchContext";
import NoImage from "../../assets/images/NoImage.webp";

const PER_PAGE = 12;

export function RateBadge({ rate }) {
    const map = {
        1: { emo: '◎', text: 'おすすめ', bg: 'var(--surface)', color: 'var(--ink)' },
        2: { emo: '◎◎', text: 'ほどよくおすすめ', bg: 'var(--hero)', color: 'var(--ink)' },
        3: { emo: '◎◎◎', text: '超おすすめ', bg: 'var(--accent)', color: 'white' },
    };
    const b = map[rate] || map[1];
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: '8px', py: '3px',
            bgcolor: b.bg, color: b.color,
            borderRadius: '999px', fontSize: 11, fontWeight: 700, lineHeight: 1.4,
        }}>
            {b.emo} {b.text}
        </Box>
    );
}

function InfoCard({ store, index, onOpen, isSelected, cardRef }) {
    return (
        <Box
            ref={cardRef}
            onClick={onOpen}
            sx={{
                display: 'flex', gap: 1.25, p: 1.25,
                bgcolor: isSelected ? 'var(--hero)' : 'var(--paper)',
                border: '1px solid',
                borderColor: isSelected ? 'var(--accent)' : 'var(--line)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'background 0.15s, border-color 0.15s',
                '&:hover': { bgcolor: 'var(--hero)', borderColor: 'var(--accent)' },
            }}
        >
            <Box sx={{
                width: 84, minWidth: 84, height: 76,
                borderRadius: '10px',
                overflow: 'hidden',
                flexShrink: 0,
                bgcolor: 'var(--surface-2)',
            }}>
                <img
                    src={store.image || NoImage}
                    alt={store.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                    <Box sx={{
                        width: 18, height: 18, minWidth: 18,
                        borderRadius: '50%',
                        bgcolor: 'var(--accent)', color: 'white',
                        fontSize: 9, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        {index + 1}
                    </Box>
                    <Typography sx={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 14, fontWeight: 700,
                        color: 'var(--ink)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {store.name}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 11, color: 'var(--muted)', mb: 0.75 }}>
                    {store.category}
                </Typography>
                <RateBadge rate={store.recommend} />
            </Box>
        </Box>
    );
}

function StoreInfo({ selectedStoreId }) {
    const [selectedStore, setSelectedStore] = useState(null);
    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const cardRefs = useRef({});

    const { searchKeyword, searchCategory, searchArea, searchRecommend, searchEatinTakeout, searchSort } = useSearchContext();

    const [storeList, setStoreList] = useState([]);
    const [totalCount, setTotalCount] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const pageRef = useRef(1);
    const isFetchingRef = useRef(false);
    const sentinelRef = useRef(null);

    const fetchPage = useCallback(async (page, replace = false) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setIsLoading(true);

        try {
            const data = await getPageInfo(
                searchKeyword, searchCategory, searchArea,
                searchRecommend, searchEatinTakeout,
                page, PER_PAGE, searchSort
            );
            const newItems = data?.page_info || [];
            const totalPage = data?.total_page || 0;

            if (replace) {
                setStoreList(newItems);
                setTotalCount(data?.total_count ?? null);
            } else {
                setStoreList(prev => [...prev, ...newItems]);
            }

            const nextPage = page + 1;
            pageRef.current = nextPage;
            setHasMore(nextPage <= totalPage && newItems.length > 0);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, [searchKeyword, searchCategory, searchArea, searchRecommend, searchEatinTakeout, searchSort]);

    useEffect(() => {
        pageRef.current = 1;
        isFetchingRef.current = false;
        setHasMore(true);
        fetchPage(1, true);
    }, [fetchPage]);

    const fetchAll = useCallback(async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setIsLoading(true);
        try {
            const data = await getPageInfo(
                searchKeyword, searchCategory, searchArea,
                searchRecommend, searchEatinTakeout,
                1, 9999, searchSort
            );
            const items = data?.page_info || [];
            setStoreList(items);
            setTotalCount(data?.total_count ?? null);
            setHasMore(false);
            pageRef.current = 2;
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, [searchKeyword, searchCategory, searchArea, searchRecommend, searchEatinTakeout, searchSort]);

    useEffect(() => {
        if (!selectedStoreId) return;
        const el = cardRefs.current[selectedStoreId];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (hasMore) {
            fetchAll();
        }
    }, [selectedStoreId, storeList, hasMore, fetchAll]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) {
                    fetchPage(pageRef.current, false);
                }
            },
            { rootMargin: '400px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, fetchPage]);

    return (
        <Box sx={{ bgcolor: 'var(--paper)', pb: 2 }}>
            {/* リストヘッダー */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--line)' }}>
                <Typography sx={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 15, fontWeight: 700, color: 'var(--ink)',
                }}>
                    {totalCount !== null ? `${totalCount}件のお店` : 'お店一覧'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--muted)', mt: 0.25 }}>
                    {searchSort === 'updated' ? '更新日順' : 'おすすめ順'}
                </Typography>
            </Box>

            {/* カードリスト */}
            <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {storeList.map((store, index) => (
                    <InfoCard
                        key={store.id ?? index}
                        store={store}
                        index={index}
                        isSelected={store.id === selectedStoreId}
                        cardRef={el => { cardRefs.current[store.id] = el; }}
                        onOpen={() => {
                            setSelectedStore(store);
                            setIsDetailVisible(true);
                        }}
                    />
                ))}
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: 'var(--accent)' }} />
                </Box>
            )}

            {!isLoading && !hasMore && storeList.length > 0 && (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: 'var(--muted)' }}>
                        全 {storeList.length} 件を表示しました
                    </Typography>
                </Box>
            )}

            {!isLoading && storeList.length === 0 && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 24, mb: 1 }}>🔍</Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--muted)' }}>
                        該当するお店が見つかりませんでした
                    </Typography>
                </Box>
            )}

            <div ref={sentinelRef} style={{ height: 1 }} />

            {isDetailVisible && selectedStore && (
                <DetailScreen
                    id={selectedStore.id}
                    onClose={() => {
                        setSelectedStore(null);
                        setIsDetailVisible(false);
                    }}
                />
            )}
        </Box>
    );
}

export default StoreInfo;
